import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, GlobalStyles, Typography } from '@mui/material';

import DataGrid from '~/components/General/OlcDataGrid';
import NoRecords from '~/components/General/NoRecords';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import TruncatedText from '~/components/General/TruncatedText';

import { fetchQrCodeScannedContacts } from '~/redux/actions/qr-codes-actions';

import { DEFAULT_PAGINATION, ENGAGEMENT_TYPES } from '~/utils/constants';

import { CustomFieldIcon } from '~/assets/images';

const getHistoryContact = (row) => row?.contact || row?.contactData || {};

const PurlHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const scannedContacts = useSelector((state) => state.qrCodesReducers.scannedContacts);
  const totalCount = useSelector((state) => state.qrCodesReducers.scannedContactsRowsCount);
  const loading = useSelector((state) => state.qrCodesReducers.scannedContactsLoader);
  const from = useSelector((state) => state.qrCodesReducers.scannedContactsFrom);
  const to = useSelector((state) => state.qrCodesReducers.scannedContactsTo);
  const lastPage = useSelector((state) => state.qrCodesReducers.scannedContactsLastPage);
  const currentPage = useSelector((state) => state.qrCodesReducers.scannedContactsCurrentPage);
  const perPage = useSelector((state) => state.qrCodesReducers.scannedContactsPerPage);

  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [loadedRequestKey, setLoadedRequestKey] = useState(null);

  const purlId = params.id;
  const requestKey = `${purlId}:${pagination.page}:${pagination.pageSize}`;

  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!purlId) return;

      await dispatch(
        fetchQrCodeScannedContacts(purlId, {
          page: pagination.page,
          pageSize: pagination.pageSize,
          engagementType: ENGAGEMENT_TYPES.PURL,
        })
      );

      if (isActive) {
        setLoadedRequestKey(requestKey);
      }
    };

    if (purlId) {
      loadHistory();
    }

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, purlId]);

  const showLoader = loadedRequestKey !== requestKey || loading;
  const pageSizes = [20, 50, 100];

  const handleChangePage = (_event, newPage) => {
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleBackToPurls = () => {
    navigate('/engagement/purls');
  };

  const columns = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          text={`${getHistoryContact(params.row).firstName || ''} ${
            getHistoryContact(params.row).lastName || ''
          }`.trim()}
          maxLength={30}
        />
      ),
    },
    {
      field: 'companyName',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText text={getHistoryContact(params.row).companyName || ''} maxLength={30} />
      ),
    },
    {
      field: 'mailingAddress',
      headerName: 'Mailing Address',
      flex: 1.5,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          htmlElement={
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {(getHistoryContact(params.row).address1 || '') +
                ' ' +
                (getHistoryContact(params.row).address2 || '')}
              <br />
              {(getHistoryContact(params.row).city || '') +
                ', ' +
                (getHistoryContact(params.row).state || '') +
                ' ' +
                (getHistoryContact(params.row).zip || '')}
            </Typography>
          }
          text={
            `${params?.row?.address1 || ''} ${params?.row?.address2 || ''}` +
            `${params?.row?.city || ''} ` +
            `${params?.row?.state || ''} ` +
            `${params?.row?.zip || ''} `
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'utmCampaignName',
      headerName: 'PURL Name',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          text={
            params?.row?.purlName || params?.row?.campaignName || params?.row?.utmCampaignName || ''
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'totalScans',
      headerName: 'Total Visits',
      width: 120,
      sortable: false,
      renderCell: (params) => <Typography variant="body2">{params.row.totalScans || 0}</Typography>,
    },
  ];

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage qrCodeHistoryPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard qrCodeHistoryScreenCard">
          <Box className="qrHistoryHeader olcHeaderWrap">
            <OlcPageBackButton onClick={handleBackToPurls}>Back to PURLs</OlcPageBackButton>
          </Box>

          {!showLoader && scannedContacts.length === 0 ? (
            <Box>
              <NoRecords
                icon={
                  <img
                    src={CustomFieldIcon}
                    style={{ height: '33px', width: '33px' }}
                    alt="no-records"
                  />
                }
                title="No PURL History"
                message={
                  <>
                    <p style={{ margin: '0px' }}>No PURL visit history available yet.</p>
                    <p style={{ margin: '0px' }}>PURLs will appear here once they are visited.</p>
                  </>
                }
              />
            </Box>
          ) : (
            <Box
              className={`olcTableGridInternal ${showLoader ? 'table-loading' : ''} ${
                scannedContacts.length === 0 ? 'table-empty' : ''
              }`}
            >
              <Box sx={{ position: 'relative' }}>
                <DataGrid
                  rows={showLoader ? [] : scannedContacts}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pagination={false}
                  rowHeight={61}
                  loading={showLoader}
                  getRowClassName={() => ''}
                />
              </Box>

              {(showLoader || totalCount > 0) && (
                <OlcTablePagination
                  count={lastPage || 1}
                  page={currentPage || 1}
                  pageSize={perPage || pagination.pageSize}
                  pageSizes={pageSizes}
                  onPageChange={handleChangePage}
                  onPageSizeChange={(event) => {
                    setPagination({ page: 1, pageSize: event.target.value });
                  }}
                  from={from}
                  to={to}
                  total={totalCount}
                  loading={showLoader}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default PurlHistory;
